import { SOURCE_PLATFORM_TYPE } from '@/entities/public-filter/constants';
import z from 'zod';

import { CAMPAIGN_LABEL_TYPE, CampaignStatus, CampaignTrackingItemType } from './constant';

export const campaignTrackHandleSchema = z.object({
  type: z.literal(CampaignTrackingItemType.HANDLE),
  handle: z.string().trim().min(1),
  platform: z.enum(SOURCE_PLATFORM_TYPE),
  label: z.enum(CAMPAIGN_LABEL_TYPE).optional(),
});

export const campaignTrackPostSchema = z.object({
  type: z.literal(CampaignTrackingItemType.POST),
  platform: z.enum(SOURCE_PLATFORM_TYPE).optional(),
  post_url: z.url(),
});

export const campaignTrackItemSchema = z.union([
  campaignTrackHandleSchema,
  campaignTrackPostSchema,
]);

export const createCampaignPostRequestSchema = z.object({
  post_url: z.url(),
  platform: z.enum(SOURCE_PLATFORM_TYPE),
});

export const campaignKeywordsSchema = z.object({
  keywords: z.array(z.string().trim().min(1)).nullable().optional(),
  hashtags: z.array(z.string().trim().min(1)).nullable().optional(),
  mentions: z.array(z.string().trim().min(1)).nullable().optional(),
});

function normalizeTrackingTerms(values?: string[] | null) {
  const seen = new Set<string>();

  return (values ?? []).flatMap((value) => {
    const normalizedValue = value.trim();
    if (!normalizedValue) return [];

    const dedupeKey = normalizedValue.toLowerCase();
    if (seen.has(dedupeKey)) return [];

    seen.add(dedupeKey);
    return [normalizedValue];
  });
}

export function mergeResponseTrackingTerms(args: z.infer<typeof campaignKeywordsSchema>): string[] {
  return normalizeTrackingTerms([
    ...normalizeTrackingTerms(args.keywords),
    ...normalizeTrackingTerms(args.hashtags).map((value) => `#${value.replace(/^#+/, '')}`),
    ...normalizeTrackingTerms(args.mentions).map((value) => `@${value.replace(/^@+/, '')}`),
  ]);
}

export const campaignBaseSchema = z.object({
  id: z.string(),
  name: z.string(),
  status: z.enum(CampaignStatus),
  start_date: z.string(),
  end_date: z.string().optional(),
  platforms: z.array(z.enum(SOURCE_PLATFORM_TYPE)).optional(),
  created_at: z.string(),
});

export const campaignSchema = z.object({
  ...campaignBaseSchema.shape,
  creator_tracking_keywords: z.array(z.string()).optional(),
  updated_at: z.string().optional(),
});

export const campaignDtoSchema = z.object({
  ...campaignBaseSchema.shape,
  ...campaignKeywordsSchema.shape,
  updated_at: z.string().optional(),
});

export function mapCampaignDtoToCampaign(
  campaignDto: z.infer<typeof campaignDtoSchema>,
): z.infer<typeof campaignSchema> {
  return campaignSchema.parse({
    id: campaignDto.id,
    name: campaignDto.name,
    status: campaignDto.status,
    start_date: campaignDto.start_date,
    end_date: campaignDto.end_date,
    platforms: campaignDto.platforms,
    creator_tracking_keywords: mergeResponseTrackingTerms({
      keywords: campaignDto.keywords,
      hashtags: campaignDto.hashtags,
      mentions: campaignDto.mentions,
    }),
    created_at: campaignDto.created_at,
  });
}

export const campaignResponseSchema = campaignDtoSchema.transform((campaignDto) =>
  mapCampaignDtoToCampaign(campaignDto),
);

export const campaignDetailCreatorSchema = z.object({
  id: z.string(),
  nickname: z.string(),
  username: z.string(),
  profile_image_url: z.url().optional(),
  email: z.string().nullable().optional(),
  label: z.enum(CAMPAIGN_LABEL_TYPE).optional(),
  platform: z.enum(SOURCE_PLATFORM_TYPE),
  post_count: z.number().int().nonnegative(),
});

export const campaignDetailPostEngagementSchema = z.object({
  views: z.number().int().nonnegative(),
  likes: z.number().int().nonnegative(),
  comments: z.number().int().nonnegative(),
  shares: z.number().int().nonnegative(),
  engagements: z.number().int().nonnegative(),
  er: z.number().nonnegative(),
});

export const campaignDetailPostCreatorSchema = z.object({
  id: z.string(),
  creator_name: z.string(),
  handle: z.string(),
  avatar_url: z.string().optional(),
  profile_url: z.string().url(),
});
